import React, { useEffect, useState } from 'react';
import { Typography, Grid,Box,Divider } from '@mui/material';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { format } from "date-fns";
import { useAppContext } from '../AppContext';

const EndOfDayReport = React.forwardRef(({shopId,date},ref) => {
  const [reportdata, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalActualCash, setTotalActualCash] = useState(0);
  const [totalCashDifference, setTotalCashDifference] = useState(0);
  const { t } = useAppContext();

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  const printStyles = `
  @media print {
    @page {
      size: 80mm auto;
      margin: 0 !important;
      padding: 0 !important;
    }
    body {
      width: 80mm;
      margin: 0 !important;
      padding: 0 !important;
      line-height: 1.2;
    }
    .print-container {
        width: 100%;
        font-size: 12px !important; /* Base font size for print */
        min-height: unset; /* Remove any minimum height constraints */
        height: auto; /* Let height be determined by content */
        break-inside: avoid; /* Prevent page breaks within the container */
        margin: 0 !important; /* Remove margin */
        padding: 0 !important; /* Remove padding */
    }
    h3 { font-size: 20px !important; margin: 4px 0; }
    h4 { font-size: 14px !important; margin: 4px 0; }
    img { max-width: 60px; height: auto; }
    .MuiTypography-root { font-size: 12px; }
    .MuiTableCell-root { font-size: 12px;padding: 2px 8px !important; }
    .MuiGrid-item { font-size: 12px;padding: 2px !important; }
  }
  
    `;
  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('Fetching data for shop:', shopId);
        let totalIncome = 0;
        let totalCash = 0;
        let totalKpay = 0;
        let totalWavePay = 0;
        let totalReceived = 0;
        let totalReceivable = 0;
        let todaySale = 0;
        let debtReceive = 0;
        let todayOrders = 0;
        let todayOrdersDebtAmount = 0;
        let todayOrdersDeliver = 0;
        let todayOrdersDeliverAmount = 0;
        let todayOutcomes = 0;
        const salesByCategory = {};
        const salesByItem = {};

        const today = date;
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        const salesQuery = query(
          collection(db, 'cashbook'), 
          where('timestamp', '>=', Timestamp.fromDate(today)),
          where('timestamp', '<', Timestamp.fromDate(tomorrow)),
          where('shopId', '==', shopId)
        );

        const orderQuery = query(
          collection(db, 'orders'), 
          where('orderDate', '>=', Timestamp.fromDate(today)),
          where('orderDate', '<', Timestamp.fromDate(tomorrow)),
          where('shopId', '==', shopId)
        );

        const todayFulfillmentQuery = query(
          collection(db, 'orders'), 
          where('fulfillmentTime', '>=', Timestamp.fromDate(today)),
          where('fulfillmentTime', '<', Timestamp.fromDate(tomorrow)),
          where('shopId', '==', shopId),
        );

        const shiftsQuery = query(
          collection(db, 'shifts'),
          where('shiftStartTime', '>=', Timestamp.fromDate(today)),
          where('shiftEndTime', '<', Timestamp.fromDate(tomorrow)),
          where('shopId', '==', shopId),
          where('isOnShift','==',false)
        );

        const [salesSnapshot, shiftsSnapshot, orderSnapshot, todayFulfillmentSnapshot] = await Promise.all([
          getDocs(salesQuery),
          getDocs(shiftsQuery),
          getDocs(orderQuery),
          getDocs(todayFulfillmentQuery)
        ]);

        // Process sales data
        salesSnapshot.forEach((doc) => {
          const sale = doc.data();
          if(sale.transactionType === 'income') {
            totalIncome += sale.amount;

          if (sale.paymentMethod === 'Cash') {
            totalCash += sale.amount;
          } else if (sale.paymentMethod === 'Kpay') {
            totalKpay += sale.amount;
          } else if (sale.paymentMethod === 'WavePay') {
            totalWavePay += sale.amount;
          }

          if (sale.description.includes('Outstanding')) {
            debtReceive += Number(sale.amount);
          } else {
            todaySale += Number(sale.amount);
          }
          }
          else
          {
            todayOutcomes += sale.amount;
          }
          
        });

        // Process Today Order to Deliver Quantity and Amount
        todayFulfillmentSnapshot.forEach((doc) => {
          const order = doc.data();
         
          if (order.orderStatus !== 'Canceled') {
            todayOrders += 1;
            //todayOrdersDebtAmount += Number(order.total) - Number(order.paymentAmount);
            todayOrdersDebtAmount+=Number(order.remainingBalance);
          }
          
          if (order.orderStatus === 'Completed') {
            const completionDate = order.completionDate.toDate();
            const startOfDay = Timestamp.fromDate(today);
            const endOfDay = Timestamp.fromDate(tomorrow);
          
            if (completionDate >= startOfDay.toDate() && completionDate <= endOfDay.toDate()) {
              todayOrdersDeliver += 1;
             // todayOrdersDeliverAmount += Number(order.total-order.paymentAmount);
             todayOrdersDeliverAmount+=Number(order.remainingBalance);
            }
          }
        });

        // Process Orders Data
        orderSnapshot.forEach((doc) => {
          const order = doc.data();
          order.cartItems.forEach((item) => {
            // Aggregate sales by category
            if (!salesByCategory[item.category]) {
              salesByCategory[item.category] = { quantity: 0, totalIncome: 0 };
            }
            salesByCategory[item.category].quantity += item.quantity;
            salesByCategory[item.category].totalIncome += item.totalPrice;

            // Aggregate sales by item
            const itemKey = `${item.name} (${item.selectedVariant})`;
            if (!salesByItem[itemKey]) {
              salesByItem[itemKey] = { quantity: 0, totalIncome: 0 };
            }
            salesByItem[itemKey].quantity += item.quantity;
            salesByItem[itemKey].totalIncome += item.totalPrice;
          });
        });

        console.log('Shift Snapshot:', shiftsSnapshot.docs.map(doc => doc.data()));

        // Process shift data
        const shifts = shiftsSnapshot.docs.map((doc, index) => {
          const shift = doc.data();
          console.log('Shift:', shift);
          return {
            name: shift.shiftName || `Shift #${index + 1}`,
            username: shift.userName,
            balance: (shift.openingCash + shift.cashPayments + shift.paidIn) - (shift.paidOut + shift.refunds),
            actualCash: shift.actualCash,
            cashDifference: shift.cashDifference,
          };
        });

        // Calculate totals
       // const shiftTotals = () => {
          const totals = shifts.reduce((acc, shift) => (
            {
            totalActualCash: acc.totalActualCash + (Number(shift.actualCash) || 0),
            totalCashDifference: acc.totalCashDifference + (Number(shift.cashDifference) || 0)
            }
        ), { totalActualCash: 0, totalCashDifference: 0 });
        
          setTotalActualCash(totals.totalActualCash);
        //  setTotalCashDifference(totals.totalCashDifference);
       // };
        


        setReportData({
          totalIncome,
          totalCash,
          totalKpay,
          totalWavePay,
          salesByCategory: Object.entries(salesByCategory).map(([name, data]) => ({ name, ...data })),
          salesByItem: Object.entries(salesByItem).map(([name, data]) => ({ name, ...data })),
          totalReceived,
          totalReceivable,
          todaySale,
          debtReceive,
          todayOrders,
          todayOrdersDebtAmount,
          todayOrdersDeliver,
          todayOrdersDeliverAmount,
          shifts,
          todayOutcomes
        });
        setLoading(false);
        console.log('Data:', shopId);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [shopId]);

  if (loading) {
    return <Typography>{t("Loading...")}</Typography>;
  }

  if (error) {
    return <Typography color="error">{t("Error")}: {error}</Typography>;
  }

  if (!shopId) {
    return <Typography>{t("No data available")}</Typography>;
  }

  return (
   
    <div ref={ref} className="print-container">
      <style>{printStyles}</style>
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography variant="h4">{t("Daily Income Summary")}</Typography>
        <Typography variant="h5">
          {format(date,"dd-MM-yyyy")}
        </Typography>
      </Box>
      <Typography variant="body" sx={{ ml: 1 }} gutterBottom>{t("Print Time")}: {format(new Date(),"dd-MM-yyyy hh:mm a")}</Typography>

      <Typography variant="h6" sx={{ ml: 1,mt:1 }} gutterBottom>{t("Cash Summary")}</Typography>
      <Grid container spacing={0} sx={{ p: 1 }}>
        <Grid item xs={6} sx={{fontWeight:'bold'}}><Typography>{t("Income")}</Typography></Grid>
        <Grid item xs={6} textAlign="right" sx={{fontWeight:'bold'}}>{formatter.format(reportdata.totalCash)} MMK</Grid>
        <Grid item xs={6} sx={{fontWeight:'bold'}}><Typography>{t("Outcome")}</Typography></Grid>
        <Grid item xs={6} textAlign="right" sx={{fontWeight:'bold'}}>{formatter.format(reportdata.todayOutcomes)} MMK</Grid>
        <Grid item xs={6} sx={{fontWeight:'bold'}}><Typography>{t("Balance")}</Typography></Grid>
        <Grid item xs={6} textAlign="right" sx={{fontWeight:'bold'}}>{formatter.format(Number(reportdata.totalCash)-Number(reportdata.todayOutcomes))} MMK</Grid>
        <Grid item xs={6} sx={{fontWeight:'bold'}}><Typography>{t("Total Actual Cash")}</Typography></Grid>
        <Grid item xs={6} textAlign="right" sx={{fontWeight:'bold'}}>{formatter.format(Number(totalActualCash))} MMK</Grid>
        <Grid item xs={6} sx={{fontWeight:'bold'}}><Typography>{t("Cash Difference")}</Typography></Grid>
        <Grid item xs={6} textAlign="right" sx={{fontWeight:'bold'}}>{formatter.format(Number(totalActualCash)-(Number(reportdata.totalCash)-Number(reportdata.todayOutcomes)))} MMK</Grid>
      </Grid>
      <Divider sx={{ my: 2 }} />
      <Typography variant="h6" sx={{ ml: 1 }} gutterBottom>{t("Income Summary")}</Typography>
      <Grid container spacing={0} sx={{ p: 1 }}>
        <Grid item xs={6}><Typography>{t("Today's Sales")}</Typography></Grid>
        <Grid item xs={6} textAlign="right">{formatter.format(reportdata.todaySale)} MMK</Grid>
        <Grid item xs={6}><Typography>{t("Debt Received")}</Typography></Grid>
        <Grid item xs={6} textAlign="right">{formatter.format(reportdata.debtReceive)} MMK</Grid>
        <Grid item xs={6}><Typography>{t("Total")}</Typography></Grid>
        <Grid item xs={6} textAlign="right">{formatter.format(Number(reportdata.todaySale)+Number(reportdata.debtReceive))} MMK</Grid>
      </Grid>

      <Typography variant="h6" sx={{ ml: 1 }} gutterBottom>{t("Income by Payment Method")}</Typography>
      <Grid container spacing={0} sx={{ p: 1 }}>
        <Grid item xs={6}><Typography>{t("Cash")}</Typography></Grid>
        <Grid item xs={6} textAlign="right">{formatter.format(reportdata.totalCash)} MMK</Grid>
        <Grid item xs={6}><Typography>{t("Kpay")}</Typography></Grid>
        <Grid item xs={6} textAlign="right">{formatter.format(reportdata.totalKpay)} MMK</Grid>
        <Grid item xs={6}><Typography>{t("Wave Pay")}</Typography></Grid>
        <Grid item xs={6} textAlign="right">{formatter.format(reportdata.totalWavePay)} MMK</Grid>
        <Grid item xs={6}><Typography>{t("Total")}</Typography></Grid>
        <Grid item xs={6} textAlign="right">{formatter.format(reportdata.totalIncome)} MMK</Grid>
      </Grid>

      <Typography variant="h6" sx={{ ml: 1 }} gutterBottom>{t("Order Summary")}</Typography>
      <Grid container spacing={0} sx={{ p: 1 }}>
        <Grid item xs={6}><Typography>{t("Today's Orders")}</Typography></Grid>
        <Grid item xs={6} textAlign="right">{formatter.format(reportdata.todayOrders)}</Grid>
        <Grid item xs={6}><Typography>{t("Receivable Balance")}</Typography></Grid>
        <Grid item xs={6} textAlign="right">{formatter.format(reportdata.todayOrdersDebtAmount)} MMK</Grid>
        <Grid item xs={6}><Typography>{t("Today's Delivered Orders")}</Typography></Grid>
        <Grid item xs={6} textAlign="right">{formatter.format(reportdata.todayOrdersDeliver)}</Grid>
        <Grid item xs={6}><Typography>{t("Received Balance")}</Typography></Grid>
        <Grid item xs={6} textAlign="right">{formatter.format(reportdata.todayOrdersDeliverAmount)} MMK</Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ ml: 1 }}>{t("Sales by Category")}</Typography>
      <Grid container spacing={0} sx={{ p: 1}}>
        {reportdata.salesByCategory.map((category) => (
            <>
            <Grid item xs={6}>{category.name}</Grid>
            <Grid item xs={6} textAlign="right">{category.quantity}</Grid>
            </>
        ))}
      </Grid>

      <Typography variant="h6" gutterBottom sx={{  ml: 1 }}>{t("Sales by Item")}</Typography>
      <Grid container spacing={0} sx={{ p: 1 }}>
        {reportdata.salesByItem.map((item) => (
          <>
            <Grid item xs={6}>{item.name}</Grid>
            <Grid item xs={6} textAlign="right">{item.quantity}</Grid>
            </>
        ))}
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ ml: 1 }}>{t("Shifts Summary")}</Typography>
      <Grid container spacing={2} sx={{ p: 1 }}>
        {reportdata.shifts.map((shift) => (
          <>
            <Grid item xs={3}>{shift.name}</Grid>
            <Grid item xs={3}>{shift.username}</Grid>
            <Grid item xs={6} textAlign="right">{formatter.format(shift.actualCash)} MMK</Grid>
            </>
        ))}
      </Grid>
    </div>
    
  );
});

export default EndOfDayReport;
